import { useCallback, useState } from 'react';
import {
  useFetchUpgradeStatusMutation,
  useUpgradeAccountsMutation,
} from 'src/shared/services/combine-accounts/combineAccounts';
import {
  ACCOUNT_STATUS_CREATED,
  UpgradeStatusEntry,
  CombinationAccountResult,
} from 'src/shared/services/combine-accounts/types';

/** Where an upgrade request has got to, for deciding what the user is shown. */
export type UpgradePhase = 'idle' | 'submitting' | 'waiting';

/** One account the upgrade endpoint refused at submit, so it was never started. */
export interface FailedUpgrade {
  dspAccountId: string;
}

/** An upgrade still running, carried between pages so polling can continue after a navigation. */
export interface OutstandingUpgrade {
  dspAccountId: string;
}

/** An account that finished upgrading, with what the success banner needs to link to it. */
export interface UpgradedAccount {
  dspAccountId: string;
  /** Present once the upgrade completes; absent before that. Read from the status response. */
  entityId?: string;
  /**
   * The account's URL, whose origin is the host its marketplace is reachable on.
   *
   * Paired with entityId: the campaigns link needs both, so it is offered only when both arrived.
   */
  accountUrl?: string;
}

/**
 * What became of every account in one upgrade request.
 *
 * The buckets are deliberately separate rather than a success boolean. `pending` exists because
 * "not finished, and no longer being watched" is a real outcome that must not be reported as either
 * success or failure: the combination continues server-side after we stop looking.
 */
export interface UpgradeOutcome {
  /** Accounts the status endpoint reported CREATED for. Only these have actually been upgraded. */
  upgraded: UpgradedAccount[];
  /**
   * Accounts the upgrade endpoint refused at submit.
   *
   * Only submit refusals reach this bucket. Nothing reports that an accepted upgrade later failed,
   * so an account that stops short of CREATED is pending rather than failed.
   */
  failed: FailedUpgrade[];
  /** Accounts neither upgraded nor failed by the time watching stopped. */
  pending: string[];
}

/**
 * How long the user is made to wait on the choose page before the upgrade stops being followed.
 *
 * From the approved design's threshold. Past this the outcome is unknown rather than bad, so the
 * user is moved on and the accounts are reported as still upgrading. Reporting that on the landing
 * page, and continuing to follow them there, is separate work.
 */
const POLL_DEADLINE_MS = 10_000;

/** Gap between status polls. Short enough to feel responsive, long enough not to hammer the BFF. */
const POLL_INTERVAL_MS = 2_000;

/**
 * How long the watch continues once it has moved to the landing page.
 *
 * Longer than the choose page's threshold because nothing is holding the user still: the report is
 * a snackbar they can ignore, so waiting costs them nothing. Still bounded, because the status read
 * has no failure value, so an upgrade that died after being accepted would otherwise be polled for
 * ever.
 */
const FOLLOW_DEADLINE_MS = 120_000;

/**
 * Gap between polls while following in the background.
 *
 * Longer than the foreground interval deliberately. Nobody is watching a spinner tick, and at the
 * foreground rate this deadline would be sixty requests per account rather than twenty four.
 */
const FOLLOW_INTERVAL_MS = 5_000;

/**
 * Sleeps, or resolves early if the caller has given up.
 *
 * Abort has to interrupt the sleep rather than only the loop condition: a watch cancelled during
 * the gap between polls would otherwise keep the page's work alive until the full interval elapsed.
 */
const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      resolve();
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * Submits an upgrade and follows it, so the UI can report what actually happened rather than what
 * was accepted.
 *
 * <h3>Why this polls at all</h3>
 *
 * The upgrade endpoint answers 202: an account's `accepted` flag means GRS took the request, not
 * that the account was upgraded, because the combination runs asynchronously afterwards. Navigating
 * to UCM or claiming success on that response would tell a user their account had been upgraded
 * while the work was still in flight, or had since failed.
 *
 * <h3>Why watching stops rather than continuing</h3>
 *
 * The approved design shows the confirmation modal's spinner for a short upgrade and moves a long one
 * to a snackbar on the landing page. Moving there is a navigation, which unmounts the page that
 * started the upgrade, so polling cannot simply carry on. {@link runUpgrade} therefore stops at the
 * threshold and hands back the ids still running. Reporting those, and resuming the watch on the
 * landing page, is separate work: until it lands, a slow upgrade shows the spinner and then leaves
 * quietly, which is incomplete rather than wrong.
 *
 * <h3>Why there is no failure outcome</h3>
 *
 * The status endpoint reports an account's status straight through from GAMS, and GAMS has only
 * CREATED and PARTIALLY_CREATED. Nothing anywhere reports that an upgrade failed, so an upgrade that
 * failed after being accepted is indistinguishable from one that is merely slow. That is why this
 * bounds its own polling instead of waiting for a terminal state: past the threshold the outcome is
 * unknown, and unknown is reported as still running rather than guessed at either way.
 */
export const useUpgradeProgress = () => {
  const [upgradeAccounts] = useUpgradeAccountsMutation();
  const [fetchStatus] = useFetchUpgradeStatusMutation();
  const [phase, setPhase] = useState<UpgradePhase>('idle');

  /**
   * Polls until every followable account reaches CREATED or the deadline passes.
   *
   * @param watching the accepted accounts to follow
   * @param options how long to keep watching, how long to leave between rounds, and an optional
   *     signal for a caller that can be unmounted mid-watch
   * @returns the settled reads, keyed by DSP account id. An account the endpoint never reports
   *     CREATED is simply absent, so it is never mistaken for a success.
   */
  const pollUntilDeadline = useCallback(
    async (
      watching: OutstandingUpgrade[],
      options: {
        deadlineMs: number;
        intervalMs: number;
        signal?: AbortSignal;
      },
    ): Promise<Map<string, UpgradeStatusEntry>> => {
      const { deadlineMs, intervalMs, signal } = options;
      const settled = new Map<string, UpgradeStatusEntry>();
      /*
        Every accepted account is followable, because the endpoint is keyed on the
        DSP account id, which the upgrade response always returns. ARMS resolves
        that to the global account internally.
      */
      const outstanding = new Set(watching.map((entry) => entry.dspAccountId));
      const startedAt = Date.now();
      let firstPoll = true;

      while (
        outstanding.size > 0 &&
        Date.now() - startedAt < deadlineMs &&
        !signal?.aborted
      ) {
        // Polled before the first wait, so a combination that has already finished is reported at
        // once rather than after an interval the user would sit through for nothing.
        if (firstPoll) {
          firstPoll = false;
        } else {
          await wait(intervalMs, signal);
          // Re-checked after the sleep: an abort during the gap should not spend a further request.
          if (signal?.aborted) {
            break;
          }
        }

        /*
          One call per round for the whole outstanding set, with entries matched
          back on dspAccountId rather than on position. The contract does answer
          in request order, but matching on the id costs nothing and means a
          reordering would not silently attribute one account's status to another.

          No chunking is needed: the set only ever shrinks, so a poll batch is
          never larger than the upgrade request the service already accepted.

          A failing call is not a failing upgrade, so it is swallowed and retried
          next round rather than reported as any account's outcome. The same holds
          per entry: an entry carrying an error, such as an account the caller does
          not own, is left outstanding rather than settling anything, so one
          unreadable account cannot decide the others.
        */
        let results: UpgradeStatusEntry[] = [];
        try {
          const response = await fetchStatus({
            dspAccountIds: Array.from(outstanding),
          }).unwrap();
          results = response.results ?? [];
        } catch {
          continue;
        }

        results.forEach((entry) => {
          /*
            An unresolved entry carries no status and falls through here, which is
            what `resolved: false` means: ARMS cannot see the new account yet, not
            that anything has gone wrong.

            The error check is deliberate rather than redundant. ARMS cannot
            currently return both a status and an error, since its success path
            never sets an error and every error path returns without a status, so
            today this only ever rejects entries the status check would reject
            anyway. It is enforced here regardless, because the alternative is a
            deep link built from a read the service itself flagged as unreliable,
            resting on an invariant that lives in another service and is not
            visible from this file. An account settling only when its status is
            CREATED *and* nothing went wrong is the rule this loop means; leaving
            it implicit made two reviewers ask whether it held.
          */
          if (!entry.error && entry.accountStatus === ACCOUNT_STATUS_CREATED) {
            settled.set(entry.dspAccountId, entry);
            outstanding.delete(entry.dspAccountId);
          }
        });
      }

      return settled;
    },
    [fetchStatus],
  );

  /**
   * Sorts watched accounts into the outcome buckets, leaving unresolved ones for the caller.
   *
   * There is no failed bucket here on purpose. ARMS reports only CREATED and PARTIALLY_CREATED, so
   * an account whose combination fails after being accepted is indistinguishable from a slow one.
   * Calling either of them a failure would assert something nothing observed, so both stay
   * unresolved and are reported as still running.
   */
  const partition = useCallback(
    (
      watched: OutstandingUpgrade[],
      settled: Map<string, UpgradeStatusEntry>,
    ) => {
      const upgraded: UpgradedAccount[] = [];
      const unresolved: OutstandingUpgrade[] = [];

      watched.forEach((entry) => {
        const status = settled.get(entry.dspAccountId);
        if (status) {
          upgraded.push({
            dspAccountId: entry.dspAccountId,
            /*
              Both come from the status read rather than the 202: neither the
              entity nor its host is known until the account reaches CREATED.
            */
            entityId: status.regionalEntityId,
            accountUrl: status.url,
          });
        } else {
          unresolved.push(entry);
        }
      });

      return { upgraded, unresolved };
    },
    [],
  );

  /**
   * Submits an upgrade and watches it for the short window.
   *
   * @param dspAccountIds the accounts to upgrade
   * @param thresholdMs how long to watch before handing off. Defaults to the design's threshold;
   *     exists so tests can reach the hand-off branch without spending ten seconds doing it
   * @returns what is known, plus anything still running for the caller to hand on
   * @throws whatever the mutation throws when the request was refused outright, which includes GRS
   *     accepting no account at all
   */
  const runUpgrade = useCallback(
    async (
      dspAccountIds: string[],
      thresholdMs: number = POLL_DEADLINE_MS,
    ): Promise<UpgradeOutcome> => {
      setPhase('submitting');
      try {
        const { accountResults } = await upgradeAccounts({
          dspAccountIds,
        }).unwrap();

        const failed: FailedUpgrade[] = accountResults
          .filter((result: CombinationAccountResult) => !result.accepted)
          .map((result: CombinationAccountResult) => ({
            dspAccountId: result.dspAccountId,
          }));

        /*
          Every accepted account is followable, because the status read is keyed on
          the DSP account id we already hold. There is deliberately no branch on
          invitationId: GRS records no invitation when upgrading, so that field is
          always null on this lane and keying on it would leave nothing to poll.
        */
        const watched: OutstandingUpgrade[] = accountResults
          .filter((result: CombinationAccountResult) => result.accepted)
          .map((result: CombinationAccountResult) => ({
            dspAccountId: result.dspAccountId,
          }));
        if (watched.length === 0) {
          return { upgraded: [], failed, pending: [] };
        }

        setPhase('waiting');
        const settled = await pollUntilDeadline(watched, {
          deadlineMs: thresholdMs,
          intervalMs: POLL_INTERVAL_MS,
        });
        const { upgraded, unresolved } = partition(watched, settled);

        /*
          Anything unresolved at the deadline is pending, never a success or a
          failure: the combination continues server-side and no failure status
          exists to read, so the caller is told only that it is still running.
        */
        return {
          upgraded,
          failed,
          pending: unresolved.map((entry) => entry.dspAccountId),
        };
      } finally {
        setPhase('idle');
      }
    },
    [upgradeAccounts, pollUntilDeadline, partition],
  );

  /**
   * Picks up accounts another page left running, and follows them to completion.
   *
   * The counterpart to {@link runUpgrade}. That one stops at its threshold and hands back the ids
   * still going, because holding a user on the choose page any longer is worse than moving them on.
   * This resumes from those ids with nothing to submit, so the caller reporting the outcome need not
   * be the page that started it.
   *
   * @param outstanding the accounts still running, as handed over
   * @param signal aborts the watch when the caller goes away. Load-bearing rather than optional in
   *     practice: without it, leaving the page keeps a request per round in flight until the
   *     deadline, for a report nothing is left to render
   * @returns the same buckets {@link runUpgrade} uses, with `failed` always empty. Only a submit
   *     refusal can fail an upgrade, and submit already happened before these ids were handed over.
   */
  const followUpgrades = useCallback(
    async (
      outstanding: OutstandingUpgrade[],
      signal?: AbortSignal,
    ): Promise<UpgradeOutcome> => {
      const settled = await pollUntilDeadline(outstanding, {
        deadlineMs: FOLLOW_DEADLINE_MS,
        intervalMs: FOLLOW_INTERVAL_MS,
        signal,
      });
      const { upgraded, unresolved } = partition(outstanding, settled);

      return {
        upgraded,
        failed: [],
        pending: unresolved.map((entry) => entry.dspAccountId),
      };
    },
    [pollUntilDeadline, partition],
  );

  return { phase, runUpgrade, followUpgrades };
};

export default useUpgradeProgress;
